import { TWalletData } from '@/features/home/types/WalletData';
import { EnumValues } from '@/services/types/EnumValues';
import {
  TScheduledBankPaymentGroupState,
  TScheduledBankPaymentGroupType,
  TScheduledPaymentIntervalType
} from '@/features/payments/types/BatchPayment';
import { BatchPaymentConfig } from '@/features/payments/batch/utils/paymentFormHelpers';

export type TBatchPayment = {
  id: string;
  createdDate: string;
  batchName?: string | null;
  scheduledDate?: string | null;
  amountOfPayments: string;
  payFromAccount?: TWalletData | null;
  totalAmount: string;
  status: BatchStatus;
  createdBy?: string | null;
  actionedBy?: string | null;
  reason?: string;
  source: string;
};

export const BatchStatusEnum = {
  PENDING: 'pending',
  REJECTED: 'rejected',
  APPROVED: 'approved',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed'
} as const;
export type BatchStatus = EnumValues<typeof BatchStatusEnum>;

export const PaymentGroupEnum = {
  SCHEDULED: 'scheduled',
  ACTION_REQUIRED: 'action_required',
  BATCH_HISTORY: 'batch_history'
} as const;
export type PaymentGroup = EnumValues<typeof PaymentGroupEnum>;
export const PAYMENT_GROUPS: PaymentGroup[] = [
  PaymentGroupEnum.SCHEDULED,
  PaymentGroupEnum.ACTION_REQUIRED,
  PaymentGroupEnum.BATCH_HISTORY
];

/**
 * A row can represent a true multi-payee batch group, an individual scheduled payment that never
 * belonged to one (see EScheduledBankPayment.scheduledBankPaymentGroup - genuinely nullable, not
 * a "trivial 1-payment group"), or a scheduled wallet-to-wallet transfer (see
 * ScheduledWalletTransferController). `pending_scheduled_payment` is the special case of a
 * scheduled payment still awaiting multi-sign approval: no EScheduledBankPayment row exists yet
 * at that point, so the row is sourced from the multi-sign action itself rather than the
 * payments list. `scheduled_transfer` never has a multi-sign equivalent - see
 * TRsScheduledWalletTransfer, which has no multiSignActionId field at all - so it only ever
 * carries the plain APPROVED ("Scheduled")/FAILED states below.
 */
export const PaymentRowKindEnum = {
  BATCH: 'batch',
  SCHEDULED_PAYMENT: 'scheduled_payment',
  PENDING_SCHEDULED_PAYMENT: 'pending_scheduled_payment',
  SCHEDULED_TRANSFER: 'scheduled_transfer'
} as const;
export type TPaymentRowKind = EnumValues<typeof PaymentRowKindEnum>;

/**
 * Error code an individual scheduled payment's delete endpoint can return (see
 * ErrorResponseBody.code), confirmed against DeactivateScheduledBankPaymentUseCaseImpl on the
 * backend - `deactivate()` only ever throws ScheduledBankPaymentNotFoundException (mapped to this
 * code) if the payment id no longer exists, e.g. already deleted/processed elsewhere in the
 * meantime, or WalletNotFoundException/PermissionRequiredException for genuine errors that fall
 * back to the generic delete-error banner same as any other unrecognised code. confirmDelete
 * treats NOT_FOUND the same as a successful delete (the desired end state - this row gone - is
 * already true) rather than showing an error there's nothing left to retry.
 *
 * Unlike a batch group (see BatchDeletionErrorCodeEnum), deactivate() has no state/processing-
 * window check at all, so it never returns anything equivalent to DELETION_NOT_ALLOWED/
 * DELETION_CONFLICT - confirmDelete only checks those against a batch row.
 */
export const ScheduledPaymentDeletionErrorCodeEnum = {
  NOT_FOUND: '4087'
} as const;

export type TPaymentListItem = {
  id: string;
  name: string;
  kind: TPaymentRowKind;
  /** Only meaningful for `kind: 'batch'` (xero/emerge); other kinds show an initials avatar. */
  type?: TScheduledBankPaymentGroupType;
  /**
   * Undefined for `kind: 'batch'` (a group has no single interval - each payment inside it does).
   * Drives the Type column for `scheduled_payment`/`pending_scheduled_payment` ("Scheduled" vs
   * "Recurring scheduled"), and, together with `paymentIntervalValue` below, the Scheduled date
   * column's recurrence annotation for every non-batch kind including `scheduled_transfer` (see
   * buildRecurringDateText) - `ONE_OFF`/undefined renders a plain date, anything else appends
   * "(Every ...)".
   */
  paymentIntervalType?: TScheduledPaymentIntervalType;
  /**
   * Paired with `paymentIntervalType` above - only meaningful when that isn't `ONE_OFF`/undefined.
   * Needed to distinguish "every week" from "every 3 weeks", or "every day" from "every 5 days",
   * in the Scheduled date column's recurrence annotation (see buildRecurringDateText).
   */
  paymentIntervalValue?: number;
  /**
   * Maps to the API's `lastSyncDate` for groups. Individual scheduled payments don't expose a
   * created timestamp yet (not mapped through by the backend) - null there, rendered as '-'.
   */
  createdDate: string | null;
  /** Maps to the API's `firstPaymentDate`. */
  scheduledDate: string;
  totalAmount: number;
  walletId: string;
  payFromAccount?: TWalletData | null;
  /**
   * `kind: 'scheduled_transfer'` only - the destination wallet id (TRsScheduledWalletTransfer's
   * `toWalletId`). The backend only ever returns the id, not a display name, so `name` above is
   * resolved from this against the wallet list once fetched (see useManagePayment's rowsByGroup),
   * the same deferred-lookup pattern `payFromAccount` already uses for `walletId`.
   */
  toWalletId?: string;
  /** `kind: 'scheduled_transfer'` only - the optional note attached when the transfer was created. */
  note?: string | null;
  /**
   * Reused as-is for `scheduled_payment` (always APPROVED - it's already past approval, just
   * upcoming) and `pending_scheduled_payment` (always PENDING_APPROVAL) rows, since both map
   * onto an existing group lifecycle state exactly - this lets every state-keyed lookup
   * (STATUS_LABEL, ACTION_BY_STATE, tab bucketing) work unchanged across all three kinds.
   */
  state: TScheduledBankPaymentGroupState;
  totalPayments: number;
  /**
   * Only meaningful for `kind: 'batch'` when state is FAILED/COMPLETED_WITH_FAILURES - together
   * they distinguish all-failed from partially-failed, since that split can't be trusted from
   * `state` alone (both counts come from the same group response).
   */
  failedPayments?: number;
  completedPayments?: number;
  /**
   * Set once a FAILED/COMPLETED_WITH_FAILURES batch has been force-completed via Mark as
   * complete - once true, Mark as complete/Retry payments are both permanently retired for it.
   */
  forceCompleted?: boolean;
  /** Only populated for `emerge` type groups; xero groups don't have this yet. */
  createdByCustomerId?: string;
  /** Set once someone has approved the group; absent while still awaiting approval. */
  approverCustomerId?: string;
  /** Id of the multi-sign action gating this group's approval; used to authorise/reject it. */
  multiSignActionId?: string;
  /**
   * Set immediately on reject (see confirmReject in useScheduledPaymentAction) so the reason
   * shows up in the list/modal right away, without waiting on a fresh fetch of the multi-sign
   * action's authorisations - useMultiSignApprovers caches that list per multiSignActionId for
   * the session, so re-fetching it right after rejecting would just return the stale
   * pre-rejection data. Not populated from the initial list fetch (the group endpoint doesn't
   * expose it) - BatchOverviewModal falls back to the authorisations list for a batch that was
   * already rejected before this session started.
   */
  rejectionReason?: string | null;
  /**
   * Inline payee/payer statement fields for `scheduled_payment`/`pending_scheduled_payment` rows
   * - both already carry every field a group would otherwise need a per-payment fetch for, so
   * the modal reads this directly instead of calling useScheduledPaymentDetail.
   */
  paymentDetail?: TScheduledPaymentDetail;
};

/** Per-payment detail within a scheduled payment group. */
export type TScheduledPaymentDetail = {
  id: string;
  payeeName: string;
  payeeBankAccount: string;
  payeeParticulars?: string | null;
  payeeAnalysis?: string | null;
  payeeReference?: string | null;
  payerParticulars?: string | null;
  payerAnalysis?: string | null;
  payerReference?: string | null;
};

/**
 * The batch payment limit, aliased from BatchPaymentConfig.MAX_PAYMENTS (the batch creation
 * workflow's own source of truth - CSV upload validation, manual-entry row cap) so this feature
 * never drifts out of sync with it. Batch Overview's payments list fetch/page size both key off
 * this same value too, so a batch never has more payments fetched than it can display on a
 * single page. Batches this large aren't expected in practice; revisit with real pagination if
 * that assumption stops holding.
 */
export const BATCH_LIMIT = BatchPaymentConfig.MAX_PAYMENTS;

/** One row of the Batch Overview payments table. */
export type TBatchOverviewPayment = {
  id: string;
  payeeName: string;
  payeeBankAccount: string;
  amount: number;
  payeeParticulars?: string | null;
  payeeAnalysis?: string | null;
  payeeReference?: string | null;
  paymentDate: string;
  /** Only meaningful once the batch has actually been sent (FAILED/COMPLETED_WITH_FAILURES). */
  lastPaymentState?: 'completed' | 'failed';
};
